// src/pages/prodactions/master-data/production-plan/ProductionPlan.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    TablePagination,
    IconButton,
    Stack,
    Snackbar,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

import { useTheme } from "@mui/material/styles";
import '../../../../components/css/ded-style.css';
import useAuthCheck from "../../../../hooks/useAuthCheck";
import { useOutletContext } from "react-router-dom";
import { database } from "../../../../firebase";
import { ref, onValue, set, remove, push } from "firebase/database";
import * as XLSX from "xlsx";

// Import formatters dari file utilitas
import {
    formatNumber,
    formatDecimal,
    formatText
} from "../../../../utils/formatters-master";

// Import fungsi formatLastUpdated dari file terpisah
import { formatLastUpdated } from "../../../../utils/formatLastUpdated";

import CustomBreadcrumbs from "../../../../components/universal-features/breadcrumbs";

// Import CustomTableContainer dari komponen terpisah
import CustomTableContainer from "../../../../components/CustomScrollContainer";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Helper function untuk mengubah Month Year menjadi format YYYY-MM untuk sorting
const parseMonthYearToSortKey = (monthYear) => {
    if (!monthYear) return "0000-00";
    
    const monthMap = {
        'jan': '01', 'feb': '02', 'mar': '03', 'apr': '04', 'may': '05', 'jun': '06',
        'jul': '07', 'aug': '08', 'sep': '09', 'oct': '10', 'nov': '11', 'dec': '12',
        'january': '01', 'february': '02', 'march': '03', 'april': '04', 'may': '05', 'june': '06',
        'july': '07', 'august': '08', 'september': '09', 'october': '10', 'november': '11', 'december': '12'
    };
    
    const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    
    const parts = monthYear.toLowerCase().split(' ');
    let month = '00';
    let year = '0000';
    
    for (const part of parts) {
        if (monthMap[part]) {
            month = monthMap[part];
        } else if (part.match(/^\d{4}$/)) {
            year = part;
        } else {
            // Coba cek apakah ini bulan dengan 3 huruf
            const possibleMonth = part.substring(0, 3);
            if (monthNames.includes(possibleMonth)) {
                month = monthMap[possibleMonth];
            }
        }
    }
    
    return `${year}-${month}`;
};

function ProductionPlan() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const fileInputRef = useRef(null);
    const [importLoading, setImportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [editingRow, setEditingRow] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [lastUpdated, setLastUpdated] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [isAddIconRotated, setIsAddIconRotated] = useState(false);
    
    // State untuk data referensi product specifications
    const [productSpecificationsList, setProductSpecificationsList] = useState([]);
    
    // State untuk data baru
    const [newData, setNewData] = useState({
        product_specification_id: '',
        month_year: '',
        production_plan: ''
    });

    // Fungsi untuk mendapatkan nama product specification berdasarkan ID
    const getProductSpecificationName = (id) => {
        const productSpec = productSpecificationsList.find(item => item.product_specification_id === id);
        return productSpec ? productSpec.product_specification_name : '';
    };

    // Fungsi untuk mendapatkan ID product specification berdasarkan nama (case-insensitive, trim)
    const getProductSpecificationIdByName = (nama) => {
        if (!nama || !productSpecificationsList.length) {
            console.log("Nama kosong atau daftar product specifications masih kosong");
            return '';
        }
        
        // Pastikan nama adalah string
        const namaString = String(nama);
        
        // Normalisasi nama input: hapus spasi berlebih, ubah ke lowercase
        const normalizedNama = namaString.toLowerCase().trim().replace(/\s+/g, ' ');
        
        console.log(`Mencari product specification dengan nama: "${namaString}" (normalized: "${normalizedNama}")`);
        
        // Cari exact match setelah normalisasi
        let productSpec = productSpecificationsList.find(item => {
            const itemNama = String(item.product_specification_name || '').toLowerCase().trim().replace(/\s+/g, ' ');
            return itemNama === normalizedNama;
        });
        
        // Jika tidak ditemukan, coba partial match (contains)
        if (!productSpec) {
            productSpec = productSpecificationsList.find(item => {
                const itemNama = String(item.product_specification_name || '').toLowerCase().trim();
                return itemNama.includes(normalizedNama) || normalizedNama.includes(itemNama);
            });
        }
        
        if (!productSpec) {
            console.log(`❌ Product specification "${namaString}" TIDAK DITEMUKAN dalam database`);
        } else {
            console.log(`✅ Product specification "${namaString}" ditemukan dengan ID: ${productSpec.product_specification_id}`);
        }
        
        return productSpec ? productSpec.product_specification_id : '';
    };

    // Fungsi untuk mengecek apakah kombinasi product_specification_id dan month_year sudah ada
    const isCombinationExists = (productSpecId, monthYear, excludeKey = null) => {
        if (!productSpecId || !monthYear || !originalDataList) return false;
        
        return originalDataList.some(item => 
            item.firebaseKey !== excludeKey &&
            item.product_specification_id === productSpecId &&
            item.month_year === monthYear
        );
    };

    useEffect(() => {
        const fetchAllData = async () => {
            setLoadingData(true);
            try {
                // Fetch data referensi terlebih dahulu
                await fetchProductSpecifications();
                
                // Fetch main data
                await fetchMainData();
                
            } catch (error) {
                console.error("Error fetching all data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchAllData();
        }
    }, [user]);

    const fetchProductSpecifications = async () => {
        try {
            // Fetch product specifications from db_productions/tb_product_specifications
            const productSpecRef = ref(database, "db_productions/tb_product_specifications");
            const productSpecSnapshot = await onValuePromise(productSpecRef);
            const productSpecData = productSpecSnapshot.val();
            const productSpecArray = [];
            
            if (productSpecData) {
                Object.keys(productSpecData).forEach(key => {
                    const record = productSpecData[key];
                    productSpecArray.push({ 
                        product_specification_id: record.product_specification_id || key,
                        product_specification_name: record.product_specification_name || ''
                    });
                });
            }
            
            console.log("Product specifications loaded:", productSpecArray.length, "items");
            console.log("Sample:", productSpecArray.slice(0, 3));
            
            setProductSpecificationsList(productSpecArray);

        } catch (error) {
            console.error("Error fetching product specifications:", error);
            throw error;
        }
    };

    const fetchMainData = async () => {
        try {
            // Fetch main data from db_productions/tb_production_plan
            const productionPlanRef = ref(database, "db_productions/tb_production_plan");
            const productionPlanSnapshot = await onValuePromise(productionPlanRef);
            const productionPlanData = productionPlanSnapshot.val();
            const loadedData = [];
            let latestUpdateTime = null;
            
            if (productionPlanData) {
                Object.keys(productionPlanData).forEach(key => {
                    const record = productionPlanData[key];
                    loadedData.push({ 
                        firebaseKey: key, 
                        production_plan_id: record.production_plan_id || key,
                        product_specification_id: record.product_specification_id || '',
                        month_year: record.month_year || '',
                        production_plan: record.production_plan || '',
                        last_updated: record.last_updated || null,
                        sortKey: parseMonthYearToSortKey(record.month_year || '') // Tambahkan sortKey untuk sorting
                    });
                    
                    // Cari waktu update terakhir
                    if (record.last_updated) {
                        const updateTime = new Date(record.last_updated);
                        if (!latestUpdateTime || updateTime > latestUpdateTime) {
                            latestUpdateTime = updateTime;
                        }
                    }
                });
                
                // Set last updated time
                if (latestUpdateTime) {
                    setLastUpdated(latestUpdateTime);
                }
            }

            // Sort by Month Year (terbaru ke terlama) menggunakan sortKey
            loadedData.sort((a, b) => {
                // Urutkan berdasarkan sortKey (YYYY-MM) descending (terbaru ke terlama)
                if (a.sortKey !== b.sortKey) {
                    return b.sortKey.localeCompare(a.sortKey);
                }
                // Jika month year sama, urutkan berdasarkan product_specification_id
                return a.product_specification_id.localeCompare(b.product_specification_id);
            });
            
            setOriginalDataList(loadedData);
            setDataList(loadedData);

        } catch (error) {
            console.error("Error fetching main data:", error);
            throw error;
        }
    };

    // Effect untuk melakukan pencarian saat searchQuery berubah
    useEffect(() => {
        if (searchQuery) {
            const filtered = originalDataList.filter(item => 
                getProductSpecificationName(item.product_specification_id).toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.month_year.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.production_plan.toString().includes(searchQuery)
            );
            setDataList(filtered);
        } else {
            setDataList(originalDataList);
        }
        setPage(0);
    }, [searchQuery, originalDataList, productSpecificationsList]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImportExcel = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            setSnackbar({
                open: true,
                message: "Tidak ada file yang dipilih.",
                severity: "warning",
            });
            return;
        }

        setImportLoading(true);
        setSnackbar({
            open: true,
            message: "Memulai proses import...",
            severity: "info",
        });

        try {
            const reader = new FileReader();
            
            reader.onload = async (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: "array" });

                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    
                    // Baca data dari Excel dengan opsi header:1 dan raw:false untuk mendapatkan nilai sebagai string
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
                        header: 1,
                        raw: false, // Ini penting! Memastikan data dibaca sebagai string, bukan angka
                        defval: '' // Default value untuk cell kosong
                    });
                    
                    console.log("Data Excel lengkap:", jsonData);
                    
                    // Data dimulai dari baris 6 (indeks 5 karena array mulai dari 0)
                    // Kolom A (index 0) kosong, data dimulai dari kolom B (index 1)
                    const dataRows = jsonData.slice(5); // Baris 6 = index 5
                    
                    console.log("Data rows (mulai baris 6):", dataRows);
                    console.log("Total baris data:", dataRows.length);

                    // Path database
                    const dbPath = "db_productions/tb_production_plan";
                    const tbRef = ref(database, dbPath);

                    // Ambil data yang sudah ada
                    const snapshot = await onValuePromise(tbRef);
                    const existingData = snapshot.exists() ? snapshot.val() : {};

                    let importedCount = 0;
                    let updatedCount = 0;
                    let ignoredCount = 0;
                    const errors = [];

                    // Dapatkan daftar nama product specifications yang valid dari database
                    const validProductNames = productSpecificationsList.map(p => 
                        p.product_specification_name.toLowerCase().trim()
                    );
                    
                    console.log("Product specifications tersedia:", productSpecificationsList.map(p => ({
                        id: p.product_specification_id,
                        name: p.product_specification_name
                    })));

                    for (let i = 0; i < dataRows.length; i++) {
                        const row = dataRows[i];
                        const barisKe = i + 6; // Untuk logging
                        
                        try {
                            // Validasi baris: harus ada dan memiliki setidaknya 4 kolom (A-D)
                            if (!row || !Array.isArray(row)) {
                                console.log(`Baris ${barisKe}: baris tidak valid, diabaikan`);
                                ignoredCount++;
                                continue;
                            }

                            // Ambil data dari kolom:
                            // Kolom A (index 0) - biasanya kosong, tidak dipakai
                            // Kolom B (index 1) - Product Specifications (harus berupa teks/nama)
                            // Kolom C (index 2) - Month Year
                            // Kolom D (index 3) - Production Plan
                            
                            // Pastikan kita membaca sebagai string
                            let productSpecName = '';
                            if (row[0] !== undefined && row[0] !== null) {
                                productSpecName = String(row[0]).trim();
                            }
                            
                            const monthYear = row[1] !== undefined && row[1] !== null ? String(row[1]).trim() : '';
                            const productionPlanRaw = row[2] !== undefined && row[2] !== null ? String(row[2]).trim() : '';

                            // Log untuk debugging
                            console.log(`Baris ${barisKe}:`, {
                                productSpecName: productSpecName || '(kosong)',
                                monthYear: monthYear || '(kosong)',
                                productionPlan: productionPlanRaw || '(kosong)'
                            });

                            // Validasi: productSpecName dan monthYear wajib ada
                            if (!productSpecName) {
                                console.log(`Baris ${barisKe}: Product Specifications kosong, diabaikan`);
                                ignoredCount++;
                                continue;
                            }

                            // CEK PENTING: Abaikan baris jika productSpecName berupa angka
                            // Product specification names seharusnya berupa teks, bukan angka
                            if (/^\d+$/.test(productSpecName)) {
                                console.log(`Baris ${barisKe}: Product Specifications berupa angka "${productSpecName}", diabaikan (bukan nama valid)`);
                                ignoredCount++;
                                continue;
                            }

                            // CEK PENTING: Abaikan baris jika productSpecName tidak ada dalam daftar valid
                            const normalizedName = productSpecName.toLowerCase().trim();
                            if (!validProductNames.includes(normalizedName)) {
                                console.log(`Baris ${barisKe}: Product Specifications "${productSpecName}" tidak ada dalam daftar valid, diabaikan`);
                                ignoredCount++;
                                continue;
                            }

                            if (!monthYear) {
                                console.log(`Baris ${barisKe}: Month Year kosong, diabaikan`);
                                ignoredCount++;
                                continue;
                            }

                            // Validasi format Month Year (sederhana)
                            if (monthYear.length < 6) { // Minimal "Jan 2025" = 8 karakter
                                console.log(`Baris ${barisKe}: Month Year "${monthYear}" format tidak valid, diabaikan`);
                                ignoredCount++;
                                continue;
                            }

                            // Konversi production plan ke number (jika ada)
                            let productionPlan = '';
                            let productionPlanNumeric = 0;
                            
                            if (productionPlanRaw) {
                                // Hapus karakter non-numeric kecuali titik dan koma
                                const cleanedValue = productionPlanRaw.replace(/[^\d.,]/g, '');
                                // Konversi ke number (handle format Indonesia dengan koma sebagai desimal)
                                productionPlanNumeric = parseFloat(cleanedValue.replace(/,/g, '.'));
                                if (!isNaN(productionPlanNumeric)) {
                                    productionPlan = productionPlanNumeric.toString();
                                } else {
                                    productionPlan = productionPlanRaw;
                                }
                            }

                            // PERBAIKAN 1: Jangan simpan data jika productionPlan kosong atau bernilai 0
                            if (!productionPlan || productionPlanNumeric === 0) {
                                console.log(`Baris ${barisKe}: Production Plan kosong atau bernilai 0, diabaikan`);
                                ignoredCount++;
                                continue;
                            }

                            // Cari ID berdasarkan nama product specification
                            const productSpecificationId = getProductSpecificationIdByName(productSpecName);

                            if (!productSpecificationId) {
                                console.log(`Baris ${barisKe}: Product specification "${productSpecName}" tidak ditemukan dalam database (seharusnya sudah divalidasi)`);
                                ignoredCount++;
                                continue;
                            }

                            // Cek apakah kombinasi sudah ada di database
                            let existingKey = null;
                            let existingRecord = null;
                            
                            for (const key in existingData) {
                                const record = existingData[key];
                                if (record.product_specification_id === productSpecificationId && 
                                    record.month_year === monthYear) {
                                    existingKey = key;
                                    existingRecord = record;
                                    break;
                                }
                            }

                            // Persiapkan data untuk disimpan - PERBAIKAN: production_plan_id TIDAK menggunakan kombinasi
                            const dataToSave = {
                                product_specification_id: productSpecificationId,
                                month_year: monthYear,
                                production_plan: productionPlan,
                                last_updated: new Date().toISOString()
                            };

                            if (existingKey) {
                                // Update data yang sudah ada - pertahankan production_plan_id yang lama
                                if (existingRecord.production_plan_id) {
                                    dataToSave.production_plan_id = existingRecord.production_plan_id;
                                }
                                
                                const existingItemRef = ref(database, `${dbPath}/${existingKey}`);
                                await set(existingItemRef, dataToSave);
                                updatedCount++;
                                console.log(`Baris ${barisKe}: Data diperbarui: ${existingKey}`);
                            } else {
                                // Simpan sebagai data baru dengan firebase key otomatis
                                const newKeyRef = push(tbRef);
                                const newKey = newKeyRef.key;
                                
                                // Set production_plan_id sama dengan firebaseKey
                                dataToSave.production_plan_id = newKey;
                                
                                await set(newKeyRef, dataToSave);
                                importedCount++;
                                console.log(`Baris ${barisKe}: Data baru dengan key: ${newKey}`);
                            }

                        } catch (error) {
                            console.error(`Error processing row ${barisKe}:`, error, "Row data:", row);
                            errors.push(`Error pada baris ${barisKe}: ${error.message}`);
                            ignoredCount++;
                        }
                    }

                    // Refresh data setelah import
                    await fetchMainData();

                    // Tampilkan hasil import
                    let message = `Import selesai! ${importedCount} data baru, ${updatedCount} data diperbarui.`;
                    let severity = "success";
                    
                    if (ignoredCount > 0) {
                        message += ` ${ignoredCount} baris diabaikan (data kosong/tidak valid/product specification tidak ditemukan/production plan kosong/0).`;
                        severity = "warning";
                    }
                    
                    if (errors.length > 0) {
                        console.error("Import errors:", errors);
                    }

                    setSnackbar({
                        open: true,
                        message: message,
                        severity: severity,
                    });

                    console.log(`Import summary: ${importedCount} baru, ${updatedCount} diperbarui, ${ignoredCount} diabaikan`);

                } catch (error) {
                    console.error("Gagal mengimpor Excel:", error);
                    setSnackbar({
                        open: true,
                        message: `Gagal membaca file Excel: ${error.message}`,
                        severity: "error",
                    });
                } finally {
                    setImportLoading(false);
                }
            };

            reader.onerror = (error) => {
                console.error("Error reading file:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal membaca file.",
                    severity: "error",
                });
                setImportLoading(false);
            };

            reader.readAsArrayBuffer(file);

        } catch (error) {
            console.error("Error in import process:", error);
            setSnackbar({
                open: true,
                message: "Terjadi kesalahan saat memproses file.",
                severity: "error",
            });
            setImportLoading(false);
        } finally {
            event.target.value = null;
        }
    };

    const handleExport = () => {
        try {
            // Siapkan data untuk export
            const exportData = dataList.map(item => ({
                'Product Specifications': getProductSpecificationName(item.product_specification_id),
                'Month Year': item.month_year,
                'Production Plan': item.production_plan
            }));

            // Buat worksheet
            const ws = XLSX.utils.json_to_sheet(exportData);
            
            // Buat workbook
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Production Plan");
            
            // Simpan file
            XLSX.writeFile(wb, `Production_Plan_${new Date().toISOString().split('T')[0]}.xlsx`);
            
            setSnackbar({
                open: true,
                message: "Data berhasil diexport.",
                severity: "success",
            });
            
        } catch (error) {
            console.error("Error exporting data:", error);
            setSnackbar({
                open: true,
                message: "Gagal mengexport data.",
                severity: "error",
            });
        }
    };

    const handlePrint = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Print akan dikembangkan.",
            severity: "info",
        });
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleEditClick = (row) => {
        setEditingRow(row.firebaseKey);
        setEditFormData({
            product_specification_id: row.product_specification_id,
            month_year: row.month_year,
            production_plan: row.production_plan
        });
    };

    const handleEditChange = (field, value) => {
        setEditFormData({
            ...editFormData,
            [field]: value
        });
    };

    const handleSaveEdit = async (firebaseKey) => {
        try {
            // Validasi production plan tidak boleh kosong atau 0
            if (!editFormData.production_plan || parseFloat(editFormData.production_plan) === 0) {
                setSnackbar({
                    open: true,
                    message: "Production Plan tidak boleh kosong atau bernilai 0.",
                    severity: "warning",
                });
                return;
            }

            // Validasi kombinasi tidak boleh duplikat (kecuali dirinya sendiri)
            if (isCombinationExists(editFormData.product_specification_id, editFormData.month_year, firebaseKey)) {
                setSnackbar({
                    open: true,
                    message: `Kombinasi Product Specification dan Month Year sudah ada dalam database.`,
                    severity: "warning",
                });
                return;
            }

            // Update data di Firebase dengan key yang sama
            const dataRef = ref(database, `db_productions/tb_production_plan/${firebaseKey}`);
            
            // PERBAIKAN: production_plan_id tetap menggunakan firebaseKey yang sama
            const updatedData = {
                production_plan_id: firebaseKey, // production_plan_id = firebaseKey
                product_specification_id: editFormData.product_specification_id,
                month_year: editFormData.month_year,
                production_plan: editFormData.production_plan,
                last_updated: new Date().toISOString()
            };
            
            await set(dataRef, updatedData);
            
            // Update local state
            const updatedList = dataList.map(item => {
                if (item.firebaseKey === firebaseKey) {
                    return {
                        ...item,
                        ...updatedData,
                        sortKey: parseMonthYearToSortKey(editFormData.month_year)
                    };
                }
                return item;
            });
            
            // Sort ulang setelah update
            updatedList.sort((a, b) => {
                if (a.sortKey !== b.sortKey) {
                    return b.sortKey.localeCompare(a.sortKey);
                }
                return a.product_specification_id.localeCompare(b.product_specification_id);
            });
            
            setDataList(updatedList);
            setOriginalDataList(updatedList);
            setEditingRow(null);
            
            setSnackbar({
                open: true,
                message: "Data berhasil diperbarui.",
                severity: "success",
            });
            
            // Update last updated time
            setLastUpdated(new Date());
            
        } catch (error) {
            console.error("Error updating data:", error);
            setSnackbar({
                open: true,
                message: "Gagal memperbarui data.",
                severity: "error",
            });
        }
    };

    const handleCancelEdit = () => {
        setEditingRow(null);
        setEditFormData({});
    };

    const handleDelete = async (firebaseKey, productionPlanId) => {
        if (window.confirm(`Apakah Anda yakin ingin menghapus data dengan Production Plan ID: ${productionPlanId}?`)) {
            try {
                // Hapus data dari Firebase
                const dataRef = ref(database, `db_productions/tb_production_plan/${firebaseKey}`);
                await remove(dataRef);
                
                // Update local state
                const updatedList = dataList.filter(item => item.firebaseKey !== firebaseKey);
                setDataList(updatedList);
                setOriginalDataList(updatedList);
                
                setSnackbar({
                    open: true,
                    message: "Data berhasil dihapus.",
                    severity: "success",
                });
                
                // Update last updated time
                setLastUpdated(new Date());
                
            } catch (error) {
                console.error("Error deleting data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal menghapus data.",
                    severity: "error",
                });
            }
        }
    };

    const handleAddNew = () => {
        setIsAddIconRotated(!isAddIconRotated);
        setShowAddForm(!showAddForm);
    };

    const handleNewDataChange = (field, value) => {
        setNewData({
            ...newData,
            [field]: value
        });
    };

    const handleSaveNew = async () => {
        // Validasi: product_specification_id dan month_year tidak boleh kosong
        if (!newData.product_specification_id) {
            setSnackbar({
                open: true,
                message: "Product Specification harus dipilih.",
                severity: "warning",
            });
            return;
        }

        if (!newData.month_year.trim()) {
            setSnackbar({
                open: true,
                message: "Month Year tidak boleh kosong.",
                severity: "warning",
            });
            return;
        }

        // Validasi production plan tidak boleh kosong atau 0
        if (!newData.production_plan || parseFloat(newData.production_plan) === 0) {
            setSnackbar({
                open: true,
                message: "Production Plan tidak boleh kosong atau bernilai 0.",
                severity: "warning",
            });
            return;
        }

        // Cek apakah kombinasi sudah ada
        if (isCombinationExists(newData.product_specification_id, newData.month_year)) {
            setSnackbar({
                open: true,
                message: `Kombinasi Product Specification dan Month Year sudah ada dalam database.`,
                severity: "warning",
            });
            return;
        }

        try {
            // Buat key baru menggunakan push
            const productionPlanRef = ref(database, "db_productions/tb_production_plan");
            const newKeyRef = push(productionPlanRef);
            const newKey = newKeyRef.key;

            // PERBAIKAN: production_plan_id = firebaseKey
            const dataToSave = {
                production_plan_id: newKey,
                product_specification_id: newData.product_specification_id,
                month_year: newData.month_year,
                production_plan: newData.production_plan,
                last_updated: new Date().toISOString()
            };
            
            await set(newKeyRef, dataToSave);
            
            // Update local state
            const newItem = {
                firebaseKey: newKey,
                ...dataToSave,
                sortKey: parseMonthYearToSortKey(newData.month_year)
            };
            
            const updatedList = [...originalDataList, newItem];
            
            // Sort by Month Year (terbaru ke terlama) menggunakan sortKey
            updatedList.sort((a, b) => {
                if (a.sortKey !== b.sortKey) {
                    return b.sortKey.localeCompare(a.sortKey);
                }
                return a.product_specification_id.localeCompare(b.product_specification_id);
            });
            
            setDataList(updatedList);
            setOriginalDataList(updatedList);
            
            // Reset form dan state
            setNewData({
                product_specification_id: '',
                month_year: '',
                production_plan: ''
            });
            setShowAddForm(false);
            setIsAddIconRotated(false);
            
            setSnackbar({
                open: true,
                message: "Data berhasil ditambahkan.",
                severity: "success",
            });
            
            // Update last updated time
            setLastUpdated(new Date());
            
        } catch (error) {
            console.error("Error adding data:", error);
            setSnackbar({
                open: true,
                message: "Gagal menambahkan data.",
                severity: "error",
            });
        }
    };

    const handleCancelNew = () => {
        setShowAddForm(false);
        setIsAddIconRotated(false);
        setNewData({
            product_specification_id: '',
            month_year: '',
            production_plan: ''
        });
    };

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    const displayedDataList = dataList;

    return (
        <Box sx={{ pb: 3 }}>
            {/* Breadcrumbs */}
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        Production Plan
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Cari..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            sx={{ width: 250 }}
                        />
                        {/* IconButton Plus/X untuk Tambah Data */}
                        <IconButton 
                            color="primary" 
                            size="small" 
                            onClick={handleAddNew}
                            sx={{
                                transition: 'transform 0.3s ease-in-out',
                                transform: isAddIconRotated ? 'rotate(45deg)' : 'rotate(0deg)',
                                backgroundColor: isAddIconRotated ? '#f2f9ff' : 'transparent',
                                '&:hover': {
                                    backgroundColor: isAddIconRotated ? '#f2f9ff' : 'action.hover'
                                }
                            }}
                        >
                            <AddOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handleImportButtonClick} disabled={importLoading}>
                            {importLoading ? <CircularProgress size={20} /> : <FileUploadOutlinedIcon />}
                        </IconButton>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleImportExcel}
                        />
                        <IconButton color="primary" size="small" onClick={handleExport}>
                            <FileDownloadOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handlePrint}>
                            <PrintOutlinedIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                {/* Form Tambah Data Baru */}
                {showAddForm && (
                    <Paper sx={{ p: 2, mb: 2, bgcolor: '#f2f9ff' }} elevation={0}>
                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                            Tambah Data Baru
                        </Typography>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                            <FormControl fullWidth size="small" required>
                                <InputLabel>Product Specifications</InputLabel>
                                <Select
                                    value={newData.product_specification_id}
                                    label="Product Specifications"
                                    onChange={(e) => handleNewDataChange('product_specification_id', e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>Pilih Product Specifications</em>
                                    </MenuItem>
                                    {productSpecificationsList.map((item) => (
                                        <MenuItem key={item.product_specification_id} value={item.product_specification_id}>
                                            {item.product_specification_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                label="Month Year"
                                size="small"
                                value={newData.month_year}
                                onChange={(e) => handleNewDataChange('month_year', e.target.value)}
                                required
                                fullWidth
                                placeholder="Contoh: Jan 2025"
                                error={newData.month_year && newData.product_specification_id && 
                                      isCombinationExists(newData.product_specification_id, newData.month_year)}
                                helperText={newData.month_year && newData.product_specification_id && 
                                          isCombinationExists(newData.product_specification_id, newData.month_year) ? 
                                          "Kombinasi sudah ada dalam database" : ""}
                            />
                            <TextField
                                label="Production Plan"
                                size="small"
                                value={newData.production_plan}
                                onChange={(e) => handleNewDataChange('production_plan', e.target.value)}
                                fullWidth
                                type="number"
                                required
                                error={newData.production_plan && parseFloat(newData.production_plan) === 0}
                                helperText={newData.production_plan && parseFloat(newData.production_plan) === 0 ? 
                                          "Production Plan tidak boleh 0" : ""}
                            />
                        </Stack>
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <Button variant="outlined" onClick={handleCancelNew}>
                                Batal
                            </Button>
                            <Button 
                                variant="contained" 
                                onClick={handleSaveNew} 
                                disabled={
                                    !newData.product_specification_id || 
                                    !newData.month_year || 
                                    !newData.production_plan ||
                                    parseFloat(newData.production_plan) === 0 ||
                                    (newData.month_year && newData.product_specification_id && 
                                     isCombinationExists(newData.product_specification_id, newData.month_year))
                                }
                            >
                                Simpan
                            </Button>
                        </Stack>
                    </Paper>
                )}

                {displayedDataList.length > 0 ? (
                    <>
                        {/* Gunakan CustomTableContainer di sini */}
                        <CustomTableContainer
                            sx={{ 
                                maxHeight: 500,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1
                            }}
                            hoverScrollbar={true}
                            scrollbarHeight="8px"
                            scrollbarWidth="8px"
                            scrollbarThumbColor="#c1c1c1"
                            scrollbarHoverColor="#a8a8a8"
                        >
                            <Table stickyHeader aria-label="production plan table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell className="ded-tch">Product Specifications</TableCell>
                                        <TableCell className="ded-tch">Month Year</TableCell>
                                        <TableCell className="ded-tch">Production Plan</TableCell>
                                        <TableCell className="ded-tch" style={{ width: '100px' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {displayedDataList
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row) => (
                                            <TableRow key={row.firebaseKey}>
                                                {editingRow === row.firebaseKey ? (
                                                    <>
                                                        <TableCell>
                                                            <FormControl fullWidth size="small">
                                                                <Select
                                                                    value={editFormData.product_specification_id}
                                                                    onChange={(e) => handleEditChange('product_specification_id', e.target.value)}
                                                                >
                                                                    <MenuItem value="">
                                                                        <em>Pilih Product Specifications</em>
                                                                    </MenuItem>
                                                                    {productSpecificationsList.map((item) => (
                                                                        <MenuItem key={item.product_specification_id} value={item.product_specification_id}>
                                                                            {item.product_specification_name}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.month_year}
                                                                onChange={(e) => handleEditChange('month_year', e.target.value)}
                                                                fullWidth
                                                                required
                                                                error={editFormData.product_specification_id && 
                                                                      editFormData.month_year && 
                                                                      isCombinationExists(
                                                                          editFormData.product_specification_id, 
                                                                          editFormData.month_year, 
                                                                          editingRow
                                                                      )}
                                                                helperText={editFormData.product_specification_id && 
                                                                           editFormData.month_year && 
                                                                           isCombinationExists(
                                                                               editFormData.product_specification_id, 
                                                                               editFormData.month_year, 
                                                                               editingRow
                                                                           ) ? 
                                                                           "Kombinasi sudah ada" : ""}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.production_plan}
                                                                onChange={(e) => handleEditChange('production_plan', e.target.value)}
                                                                fullWidth
                                                                type="number"
                                                                required
                                                                error={editFormData.production_plan && parseFloat(editFormData.production_plan) === 0}
                                                                helperText={editFormData.production_plan && parseFloat(editFormData.production_plan) === 0 ? 
                                                                          "Production Plan tidak boleh 0" : ""}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1}>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="success"
                                                                    onClick={() => handleSaveEdit(row.firebaseKey)}
                                                                    disabled={
                                                                        !editFormData.product_specification_id || 
                                                                        !editFormData.month_year || 
                                                                        !editFormData.production_plan ||
                                                                        parseFloat(editFormData.production_plan) === 0 ||
                                                                        (editFormData.product_specification_id && 
                                                                         editFormData.month_year && 
                                                                         isCombinationExists(
                                                                             editFormData.product_specification_id, 
                                                                             editFormData.month_year, 
                                                                             editingRow
                                                                         ))
                                                                    }
                                                                >
                                                                    <CheckOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="error"
                                                                    onClick={handleCancelEdit}
                                                                >
                                                                    <CloseOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                    </>
                                                ) : (
                                                    <>
                                                        <TableCell>
                                                            <Box>
                                                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                                    {formatText(getProductSpecificationName(row.product_specification_id))}
                                                                </div>
                                                                <div style={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                                                    Product Specifications Id: {formatText(row.product_specification_id)}
                                                                </div>
                                                                <div style={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                                                    Production Plan Id: {formatText(row.firebaseKey)}
                                                                </div>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>{formatText(row.month_year)}</TableCell>
                                                        <TableCell>{formatNumber(row.production_plan)}</TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1}>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="primary"
                                                                    onClick={() => handleEditClick(row)}
                                                                >
                                                                    <EditOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="error"
                                                                    onClick={() => handleDelete(row.firebaseKey, row.production_plan_id)}
                                                                >
                                                                    <DeleteOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                    </>
                                                )}
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </CustomTableContainer>
                        
                        {/* Container untuk TablePagination dan Last Updated Info */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            {/* Bagian kiri: Info Data diupdate terakhir */}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                Data diupdate terakhir: {formatLastUpdated(lastUpdated)}
                            </Typography>
                            
                            {/* Bagian kanan: TablePagination */}
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={displayedDataList.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{ 
                                    flexShrink: 0,
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        margin: 0
                                    }
                                }}
                            />
                        </Box>
                    </>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: 200,
                        border: '1px dashed #e0e0e0',
                        borderRadius: 1
                    }}>
                        <Typography variant="body1" color="text.secondary">
                            {searchQuery ? 'Tidak ada data yang sesuai dengan pencarian' : 'Tidak ada data. Klik icon "+" untuk menambahkan atau "Import Excel" untuk mengimpor data.'}
                        </Typography>
                    </Box>
                )}
            </Paper>
            
            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default ProductionPlan;